import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Plus, X } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import { extractVariables, DEFAULT_VARIABLES } from '../utils/templateVars';

const CATEGORIES = ['nda', 'hr', 'vendor', 'legal', 'finance', 'custom'];

export default function TemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const isNew = templateId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    category: 'custom',
    description: '',
    content_html: '',
  });
  const [variables, setVariables] = useState([...DEFAULT_VARIABLES]);
  const [newVarInput, setNewVarInput] = useState('');

  useEffect(() => {
    if (isNew) return;
    API.get(`/templates/${templateId}`)
      .then(r => {
        const t = r.data;
        setForm({
          name: t.name || '',
          category: t.category || 'custom',
          description: t.description || '',
          content_html: t.content_html || '',
        });
        setVariables(t.variables?.length ? t.variables : [...DEFAULT_VARIABLES]);
      })
      .catch(() => { toast.error('Template not found'); navigate('/templates'); })
      .finally(() => setLoading(false));
  }, [templateId, isNew, navigate]);

  const handleContentChange = (html) => {
    setForm(p => ({ ...p, content_html: html }));
    const detected = extractVariables(html);
    if (detected.length) {
      setVariables(prev => [...new Set([...prev, ...detected])].sort());
    }
  };

  const addVariable = () => {
    const name = newVarInput.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, '');
    if (!name) return;
    if (!variables.includes(name)) setVariables(prev => [...prev, name].sort());
    setNewVarInput('');
  };

  const removeVariable = (name) => {
    setVariables(prev => prev.filter(v => v !== name));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, variables };
      if (isNew) {
        const r = await API.post('/templates', payload);
        toast.success('Template created');
        navigate(`/templates/${r.data.id}`, { replace: true });
      } else {
        await API.put(`/templates/${templateId}`, payload);
        toast.success('Template saved');
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 fade-in max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/templates')} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-sm">
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              {isNew ? 'New Template' : 'Edit Template'}
            </h1>
            <p className="text-sm text-slate-500">Design your document with rich formatting and variables</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium disabled:opacity-60"
          data-testid="save-template-btn"
        >
          <Save size={15} /> {saving ? 'Saving…' : 'Save Template'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Template Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Standard NDA"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
              <select
                value={form.category}
                onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white capitalize"
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="When to use this template…"
              />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Variables</h3>
            <p className="text-xs text-slate-500 mb-3">Define variables to insert into your document. Use the Insert Variable button in the editor toolbar.</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {variables.map(v => (
                <span key={v} className="inline-flex items-center gap-1 variable-chip text-xs">
                  {`{{${v}}}`}
                  <button type="button" onClick={() => removeVariable(v)} className="text-violet-400 hover:text-violet-700">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-1">
              <input
                type="text"
                value={newVarInput}
                onChange={e => setNewVarInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addVariable())}
                placeholder="e.g. company_name"
                className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-violet-400"
              />
              <button type="button" onClick={addVariable} className="p-1.5 text-violet-600 hover:bg-violet-50 rounded-sm border border-violet-200">
                <Plus size={14} />
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <RichTextEditor
            content={form.content_html}
            onChange={handleContentChange}
            variables={variables}
            onVariablesChange={setVariables}
            placeholder="Write your document template here. Use headings, lists, tables, and insert variables like {{company_name}}."
          />
        </div>
      </div>
    </div>
  );
}
